import { useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasTopHeaderState } from "../../../obras/presentation/components/hooks/obras_top_header_state";
import { useAuditoresPagination } from "../hooks/Auditoria_auditores/pagination";

import GppGoodIcon from '@mui/icons-material/GppGood'; 
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { PrincipalDocumentosLista } from "../../domain/models/documentos_lista";
import CommonConstants from "../../../../common/constants";
import { AuditoriaIndicadores } from "../../domain/models/auditoria_indicadores_lista";

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

export const useAuditoriasPrincipalViewModel = () => {
  const {
    commons: { getAllListagemDocUseCase, getGedArquivoDataUseCase},
    reports: {},
    auditor: { getAllIndicadoresUseCase },
  } = useDependencyInjection();


  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);
  const [indicadores, setIndicadores] = useState<AuditoriaIndicadores>();
  const navigate = useNavigate();



  const {pushErrorNotification } = usePageLoader();

  const obrasTopHeaderState = useObrasTopHeaderState();
   const pagination = useAuditoresPagination();

  // #region Funções de carregamento

  const loadIndicadoresState = useCallback(async () => {
    try {
        const response =await getAllIndicadoresUseCase.getAllListagemIndicadores();
        setIndicadores(response);
    } catch (e: unknown) {
        throw new Error("Falha ao buscar documentos!");
    }
  }, [getAllIndicadoresUseCase]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemDocUseCase.getAllListagemDoc(
          String(CommonConstants.moduloDocumentos.Auditoria),
            filteredPage,
            filteredRowsPerPage
        );
        setDocLista(getAllListagemDocUseCase.items);
        pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
        pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
        throw new Error("Falha ao buscar documentos!");
    }
  }, [getAllListagemDocUseCase, pagination]);


  //#endregion
  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];

      jobs.push(loadDocListaState());
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadDocListaState());
      jobs.push(loadIndicadoresState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadDocListaState, loadIndicadoresState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [getGedArquivoDataUseCase, pushErrorNotification]);
  

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadDocListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadDocListaState, pagination, pushErrorNotification]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
  try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
  } catch (e: unknown) {
    pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
  } finally {
    setLoading(false);
  }
}, [loadDocListaState, pagination, pushErrorNotification]);


  //#endregion


  const handleNavegate = useCallback((id: number) => {
      if (id === 1)  navigate(`/Auditorias_lista/1`, )
      else if (id === 2) navigate(`/Auditorias_lista/2`, )
      else if (id === 3) navigate(`/Auditores_lista/`)
  }, [navigate]);

  const stats = useMemo(() => [
    { 
      title: 'Auditorias Em Aberto', 
      value: indicadores?.auditoriasEmAberto ?? 0, 
      icon: GppGoodIcon, 
      colorClass: { bg: 'bg-red-100', text: 'text-red-600', bgHover: 'hover:bg-red-200' },
      linkText: 'Ir para cadastro',
      onClick: () => handleNavegate(1)
    },
    { 
      title: 'Auditorias Encerradas', 
      value: indicadores?.auditoriasEncerradas ?? 0, 
      icon: GppMaybeIcon,
      colorClass: { bg: 'bg-gray-100', text: 'text-gray-600', bgHover: 'hover:bg-gray-200' },
      linkText: 'Ir para cadastro',
      onClick: () => handleNavegate(2)
    },
    { 
      title: 'Auditores Cadastrados', 
      value: indicadores?.auditoresCadastrados ?? 0, 
      icon: EventAvailableIcon,
      colorClass: { bg: 'bg-teal-100', text: 'text-teal-600', bgHover: 'hover:bg-teal-200' },
      linkText: 'Ir para cadastro', 
      onClick: () => handleNavegate(3)
    },
  ], [indicadores?.auditoriasEmAberto, indicadores?.auditoriasEncerradas, indicadores?.auditoresCadastrados, handleNavegate]);

  return {
      loading,
      errorMessage,
      obrasTopHeaderState,
      docLista,
      pagination,
      stats,
      handleNavegate,
      handleReload,
      handlePageChange,
      handleRowsPerPageChange,
      handleDownloadFile,
  }
}